/* src/components/Chat/ChatInput.js */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { EmojiPicker } from '@ferrucc-io/emoji-picker';
import PropTypes from 'prop-types';
import './ChatInput.css';

const MIN_SEND_INTERVAL = 350;
const STOP_TYPING_DELAY = 1500;
const SCROLL_THRESHOLD = 400;

const ChatInput = ({
    value,
    onChange,
    onSend,
    onTypingStart = () => { },
    onTypingStop = () => { },
    onUploadImage = () => { },
    replyTo = null,
    onCancelReply = () => { },
    scrollContainerRef
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [showPicker, setShowPicker] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const [showScrollBtn, setShowScrollBtn] = useState(false);

    const emojiRef = useRef(null);
    const inputRef = useRef(null);
    const stopTypingTimeout = useRef(null);
    const lastSendRef = useRef(0);
    const isTypingRef = useRef(false);

    useEffect(() => { setInputValue(value); }, [value]);
    useEffect(() => () => clearTimeout(stopTypingTimeout.current), []);

    const handleScrollVisibility = useCallback(() => {
        const container = scrollContainerRef?.current;
        if (!container) return;
        const isFarUp = Math.abs(container.scrollTop) > SCROLL_THRESHOLD;
        setShowScrollBtn(isFarUp);
    }, [scrollContainerRef]);

    useEffect(() => {
        const container = scrollContainerRef?.current;
        if (container) {
            container.addEventListener('scroll', handleScrollVisibility);
            return () => container.removeEventListener('scroll', handleScrollVisibility);
        }
    }, [scrollContainerRef, handleScrollVisibility]);

    const jumpToBottom = () => {
        const container = scrollContainerRef?.current;
        if (container) {
            container.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange(newValue);

        if (newValue.trim()) {
            if (!isTypingRef.current) {
                isTypingRef.current = true;
                onTypingStart();
            }
            clearTimeout(stopTypingTimeout.current);
            stopTypingTimeout.current = setTimeout(() => {
                isTypingRef.current = false;
                onTypingStop();
            }, STOP_TYPING_DELAY);
        }
    };

    const handleEmojiSelect = (emoji) => {
        if (!emoji?.native) return;
        const cursorPos = inputRef.current?.selectionStart ?? inputValue.length;
        const updated = inputValue.slice(0, cursorPos) + emoji.native + inputValue.slice(cursorPos);
        setInputValue(updated);
        onChange(updated);
        setShowPicker(false);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    /**
     * ✅ REFINED: handleSafeSend
     * Strictly ensures only a string reaches the parent 'onSend' call.
     */
    const handleSafeSend = (e) => {
        // Prevent default if an event was passed (from Enter key)
        if (e && e.preventDefault) e.preventDefault();

        // 1. Final check: Ensure we are dealing with a string
        const text = (inputValue || '').trim();
        if (!text) return;

        // 2. Rate limiting
        const now = Date.now();
        if (now - lastSendRef.current < MIN_SEND_INTERVAL) return;

        // 3. Typing status cleanup
        clearTimeout(stopTypingTimeout.current);
        if (isTypingRef.current) {
            isTypingRef.current = false;
            onTypingStop();
        }

        lastSendRef.current = now;

        // 4. Pass ONLY the text string upwards
        onSend(text);

        // 5. Reset local states
        setInputValue('');
        onChange('');
        setShowPicker(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            if (isComposing) return;
            e.preventDefault();
            handleSafeSend(e);
        } else if (e.key === 'Escape') {
            setShowPicker(false);
        }
    };

    return (
        <div className="chat-input-wrapper">
            {showScrollBtn && (
                <button className="jump-bottom-btn" onClick={jumpToBottom} title="Jump to bottom">
                    ↓
                </button>
            )}

            {replyTo && (
                <div className="reply-preview-bar">
                    <div className="reply-preview-content">
                        <span className="reply-to-user">Replying to {replyTo.sender?.username || 'User'}</span>
                        <span className="reply-text-snippet">{replyTo.content}</span>
                    </div>
                    <button className="cancel-reply-x" onClick={onCancelReply} aria-label="Cancel reply">
                        ✕
                    </button>
                </div>
            )}

            <div className="chat-input-bar">
                <button type="button" className="icon-btn" onClick={() => setShowPicker(!showPicker)}>
                    😊
                </button>

                <label className="icon-btn upload-label">
                    📎
                    <input type="file" accept="image/*" hidden onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onUploadImage(file);
                        e.target.value = null;
                    }} />
                </label>

                <input
                    ref={inputRef}
                    type="text"
                    className="chat-input-field"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    placeholder="Type a message..."
                    autoComplete="off"
                />

                <button
                    type="button"
                    className={`send-btn ${inputValue.trim() ? 'active' : ''}`}
                    // ✅ FIXED: Wrapped in arrow function to avoid passing the event object as 'text'
                    onClick={() => handleSafeSend()}
                    disabled={!inputValue.trim()}
                >
                    ➤
                </button>

                {showPicker && (
                    <div className="emoji-picker-popup" ref={emojiRef}>
                        <EmojiPicker onEmojiSelect={handleEmojiSelect} />
                    </div>
                )}
            </div>
        </div>
    );
};

ChatInput.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    onTypingStart: PropTypes.func,
    onTypingStop: PropTypes.func,
    onUploadImage: PropTypes.func,
    replyTo: PropTypes.object,
    onCancelReply: PropTypes.func,
    scrollContainerRef: PropTypes.object
};

export default ChatInput;